import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';

import {
  configurationService,
  IngestionSettings as IngestionSettingsType,
} from '../../services/configurationService';

export const IngestionSettings: React.FC = () => {
  const [settings, setSettings] = useState<IngestionSettingsType>({
    filenamePattern: '',
    ingestionEnabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const ingestionSettings = await configurationService.getIngestionSettings();
      setSettings(ingestionSettings);
      setError('');
    } catch (_err) {
      setError('Failed to load ingestion settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await configurationService.updateIngestionSettings(settings);
      setSuccess('Ingestion settings updated successfully');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (_err) {
      setError('Failed to update ingestion settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadSettings();
    setError('');
    setSuccess('');
  };

  // Client-side filename parser for live preview
  const parseFilename = (filename: string, pattern: string): Record<string, string> => {
    if (!pattern || !filename) return {};

    const result: Record<string, string> = {};
    
    // Remove file extension
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    
    // Check if pattern contains _FULL tokens (greedy parsing mode)
    const hasFullTokens = pattern.includes('_FULL');
    
    if (hasFullTokens) {
      // Right-to-left greedy parsing for patterns with spaces
      const segments = nameWithoutExt.split('-');
      
      // Extract DATE (first 8 digits)
      const dateMatch = nameWithoutExt.match(/^(\d{8})/);
      if (dateMatch) {
        result.DATE = dateMatch[1];
      }
      
      // Extract CALLTYPE (last segment)
      if (segments.length > 0) {
        result.CALLTYPE = segments[segments.length - 1];
      }
      
      // Extract OPERATOR_FULL (second-to-last segment)
      if (segments.length > 1) {
        result.OPERATOR_FULL = segments[segments.length - 2];
      }
      
      // Extract LOCATION_FULL (everything between DATE and OPERATOR_FULL)
      if (segments.length > 2) {
        const locationSegments = segments.slice(1, segments.length - 2);
        result.LOCATION_FULL = locationSegments.join('-');
      }
    } else {
      // Standard regex-based parsing
      const tokens = [
        { token: '{DATE}', regex: '(\\d{8}|\\d{4}-\\d{2}-\\d{2})', key: 'DATE' },
        { token: '{LOCATION}', regex: '([A-Za-z0-9_]+)', key: 'LOCATION' },
        { token: '{OPERATOR}', regex: '([A-Za-z]+)', key: 'OPERATOR' },
        { token: '{CALLTYPE}', regex: '([A-Z]+)', key: 'CALLTYPE' },
        { token: '{INCIDENTNUMBER}', regex: '([0-9]+)', key: 'INCIDENTNUMBER' },
        { token: '{CALLERNUMBER}', regex: '([0-9-]+)', key: 'CALLERNUMBER' },
      ];
      
      let regexPattern = pattern;
      const tokenMap: Record<string, string> = {};
      
      tokens.forEach(({ token, regex, key }) => {
        if (regexPattern.includes(token)) {
          regexPattern = regexPattern.replace(token, `(?<${key}>${regex})`);
          tokenMap[key] = token;
        }
      });
      
      // Escape remaining special characters
      regexPattern = regexPattern.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
      
      try {
        const match = nameWithoutExt.match(new RegExp(regexPattern));
        if (match && match.groups) {
          Object.assign(result, match.groups);
        }
      } catch {
        // Invalid regex pattern
      }
    }
    
    return result;
  };

  const sampleFilename = '20250416-416 Benlea Cir-Liz Rose-PHONE.wav';
  const parsedSample = parseFilename(sampleFilename, settings.filenamePattern);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        File Ingestion Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure filename parsing patterns for manual uploads and SFTP batch ingestion.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Configuration */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Ingestion Settings
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.ingestionEnabled}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        ingestionEnabled: e.target.checked,
                      }))
                    }
                  />
                }
                label="Enable File Ingestion"
                sx={{ mb: 3 }}
              />

              <TextField
                label="Filename Pattern"
                value={settings.filenamePattern}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    filenamePattern: e.target.value,
                  }))
                }
                fullWidth
                multiline
                rows={2}
                helperText="Define how filenames should be parsed to extract metadata"
                placeholder="{DATE}-{LOCATION_FULL}-{OPERATOR_FULL}-{CALLTYPE}"
              />
            </CardContent>
          </Card>

          {/* Available Tokens */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Available Tokens
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use these tokens in your filename pattern to extract metadata from uploaded files.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Chip label="{DATE}" size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    Date in yyyyMMdd or yyyy-MM-dd format (e.g., 20250416)
                  </Typography>
                </Box>

                <Box>
                  <Chip label="{LOCATION_FULL}" size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    Location with spaces and special characters (e.g., "416 Benlea Cir")
                  </Typography>
                </Box>

                <Box>
                  <Chip label="{OPERATOR_FULL}" size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    Dispatcher name with spaces (e.g., "Liz Rose")
                  </Typography>
                </Box>

                <Box>
                  <Chip label="{CALLTYPE}" size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    Call type (e.g., PHONE, RADIO)
                  </Typography>
                </Box>

                <Box>
                  <Chip label="{OPERATOR}" size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    Dispatcher name without spaces (e.g., JSmith)
                  </Typography>
                </Box>

                <Box>
                  <Chip label="{LOCATION}" size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    Location without spaces (e.g., Station5)
                  </Typography>
                </Box>

                <Box>
                  <Chip label="{INCIDENTNUMBER}" size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    Incident number (e.g., 2025001234)
                  </Typography>
                </Box>

                <Box>
                  <Chip label="{CALLERNUMBER}" size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    Caller phone number (e.g., 555-1234)
                  </Typography>
                </Box>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Pattern Examples:</strong>
                  <br />
                  • <code>{'{DATE}-{LOCATION_FULL}-{OPERATOR_FULL}-{CALLTYPE}'}</code>
                  <br />
                  • <code>{'{DATE}-{OPERATOR}-{LOCATION}-{CALLTYPE}'}</code>
                  <br />
                  • <code>{'{DATE}_{INCIDENTNUMBER}_{CALLTYPE}'}</code>
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Box>

        {/* Live Preview */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Live Preview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                See how your pattern parses a sample filename.
              </Typography>

              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sample Filename:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                >
                  {sampleFilename}
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Pattern:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                >
                  {settings.filenamePattern || '(no pattern set)'}
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Parsed Result:
                </Typography>
                {Object.keys(parsedSample).length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {Object.entries(parsedSample).map(([key, value]) => (
                      <Box key={key} sx={{ display: 'flex', gap: 1 }}>
                        <Chip label={key} size="small" color="primary" />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No fields extracted. Check your pattern syntax.
                  </Typography>
                )}
              </Paper>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> This is a client-side preview. Actual parsing may differ
                  slightly based on backend implementation.
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          {/* Parsing Guidelines */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Parsing Guidelines
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>Delimiter-Based Parsing:</strong> Use hyphens (-) or underscores (_) to
                separate fields in your pattern.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>Greedy Tokens:</strong> When using <code>_FULL</code> tokens (like{' '}
                <code>{'{LOCATION_FULL}'}</code>), the parser uses right-to-left greedy matching to
                handle spaces within field values.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>Fallback Behavior:</strong> If parsing fails, the file will still be
                ingested with basic metadata (filename, upload date, user). Parsing failures never
                block uploads.
              </Typography>

              <Typography variant="body2">
                <strong>Best Practice:</strong> Test your pattern with real filenames before
                deploying to production. Consistent naming conventions improve parsing accuracy.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save Settings'}
        </Button>
        <Button variant="outlined" onClick={handleReset} disabled={saving}>
          Reset
        </Button>
      </Box>
    </Box>
  );
};
